import React, { useState, useEffect } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import { motion } from "framer-motion"
import { formatTime, getFormattedTime } from "../entities/tracking/helpers"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"

type TimerProps = {
    handleStartTimer?: () => void
    handleStopTimer?: () => void
    isShift?: boolean
    timestamp?: Date
    onTimeUpdate?: (formattedTime: string) => void
}

const Timer = ({ handleStopTimer, isShift, timestamp, onTimeUpdate }: TimerProps) => {
    const theme = useTheme()
    const { t } = useTranslation()
    const formattedTimeStamp = timestamp ? dayjs(timestamp).valueOf() : dayjs().valueOf()
    const [timeElapsed, setTimeElapsed] = useState(() => Math.floor((dayjs().valueOf() - formattedTimeStamp) / 1000))
    const startTime = getFormattedTime(timestamp)

    // Update elapsed time every second
    useEffect(() => {
        const intervalId = setInterval(() => {
            setTimeElapsed(Math.floor((dayjs().valueOf() - formattedTimeStamp) / 1000))
        }, 1000)

        return () => clearInterval(intervalId)
    }, [formattedTimeStamp])

    // Send formatted time to parent
    useEffect(() => {
        if (onTimeUpdate) {
            onTimeUpdate(formatTime(timeElapsed))
        }
    }, [timeElapsed, onTimeUpdate, formatTime])

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor={isShift ? theme.palette.success.main : theme.palette.secondary.main}
            width="90%"
            maxWidth={700}
            position="absolute"
            borderRadius={6}
            paddingY={5}
            paddingX={2}
            flexDirection="column"
            margin="auto"
            textAlign="center"
            onClick={handleStopTimer}
        >
            {isShift && (
                <>
                    <Typography variant="h4" style={{ fontWeight: "bold" }}>
                        {t("tracking.trackingStatusActive")}
                    </Typography>

                    <Typography variant="h6">
                        {t("tracking.startTimeLabel")}: {startTime}
                    </Typography>
                </>
            )}

            <motion.div
                initial={{ opacity: 0, scale: 0.2 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
            >
                <Typography variant="h4">{formatTime(timeElapsed)}</Typography>
                {!isShift && <Typography>{t("tracking.completeActivityPrompt")}</Typography>}
            </motion.div>
        </Box>
    )
}

export default Timer
